/**
 * Reihen aufeinander stapeln (HM-054).
 *
 * Gestapelt wird, wo sich Teile zu einem Ganzen addieren — Netzbezug und
 * Eigenverbrauch ergeben zusammen den Verbrauch. Die Zeichenschicht kennt
 * keinen Stapel: Sie bekommt bereits aufsummierte Spalten und zeichnet sie von
 * der obersten zur untersten, sodass jede die darunter überdeckt.
 *
 * **Eine Lücke in einer Reihe macht den ganzen Stapel an dieser Stelle zur
 * Lücke.** Das ist die unbequeme, aber einzig ehrliche Regel (Regel 8): Fehlt
 * ein Summand, ist die Summe nicht kleiner, sondern **unbekannt**. Wer die
 * fehlende Reihe stillschweigend als null zählt, zeigt eine Summe, die zu
 * niedrig ist — und sie sieht aus wie eine Messung.
 */

/**
 * Summiert die Spalten von unten nach oben auf.
 *
 * Die Eingabe ist die Zeichenreihenfolge von unten (erste Reihe) nach oben; die
 * Ausgabe trägt an Stelle `i` die Summe der Reihen `0..i`.
 */
export function stackValues(
  columns: readonly (readonly (number | null)[])[],
): readonly (readonly (number | null)[])[] {
  const out: (number | null)[][] = [];

  for (const [index, column] of columns.entries()) {
    const below = out[index - 1];
    out.push(
      column.map((value, position) => {
        if (value === null) return null;
        if (below === undefined) return value;
        const beneath = below[position];
        return beneath === null || beneath === undefined ? null : beneath + value;
      }),
    );
  }

  return out;
}

/**
 * Der eigene Wert einer gestapelten Reihe — die Umkehrung von `stackValues`
 * für **einen** Punkt.
 *
 * Das Ablesefenster liest aus der Matrix, die gezeichnet wird, und dort steht
 * bei einem Stapel die aufsummierte Spalte. Ohne diese Rückrechnung nennt es
 * für die obere Reihe die Summe aller darunter — im Verlauf einer Wohnung also
 * für den Netzanteil den ganzen Verbrauch, und zwar unter dem Namen
 * „Netzanteil". Das ist keine Ungenauigkeit, sondern eine falsche Zahl unter
 * einer richtigen Beschriftung.
 *
 * Fehlt eine der beiden Zahlen, ist die Differenz **unbekannt** und nicht null
 * (Regel 8) — dieselbe Regel wie beim Stapeln selbst.
 */
export function unstackValue(
  value: number | null | undefined,
  beneath: number | null | undefined,
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  if (beneath === undefined) return value;
  if (beneath === null || !Number.isFinite(beneath)) return null;
  return value - beneath;
}
